/**
 * threadSections — the ONE sectioning rule for the thread lists (chat-thread
 * experience PR-5, 2026-09-09; built-ins first-class 2026-09-09). The rail,
 * the mobile sheet and the docked panel all derive their sections here, so a
 * thread lands in the same section on every surface.
 *
 * The viewer's ORDERED section list (custom categories + the three built-ins,
 * `withBuiltinSections`) decides the order top to bottom. A thread is in
 * EXACTLY ONE section: routines (the caller excludes them first — the
 * routines union is the rail's own) > the section it is FILED under (a
 * custom id or a built-in id on its own index doc; a folded alias follows)
 * > its NATURAL built-in (direct message / group thread / thread).
 *
 * A section renders only when non-empty (`includeEmpty` for the manager);
 * ordering WITHIN a section is the caller's (`orderThreads`).
 */
import {
  builtinIdForKind,
  builtinKindOf,
  categoryLabel,
  categoryOf,
  withBuiltinSections,
  type BuiltinSectionKind,
  type ThreadCategory,
  type ThreadListLang,
} from '../../types/threadCategories';
import type { ThreadDisplayKind } from '../../services/groupThreads/threadDisplayTitle';

export type ThreadSectionKind = 'category' | 'direct' | 'group' | 'threads' | 'triage';

export interface ThreadSection<T> {
  /** Stable key: `category:<id>` | 'direct' | 'group' | 'threads'. */
  key: string;
  kind: ThreadSectionKind;
  /** The label the viewer sees (their name, or the built-in's default in their language). */
  label: string;
  rows: T[];
  /** The section's id — a custom id, or the built-in's fixed id. */
  categoryId: string;
  builtin?: BuiltinSectionKind;
}

/** The English defaults (kept for callers that render a built-in's label without a viewer). */
export const THREAD_SECTION_LABELS: Record<Exclude<ThreadSectionKind, 'category'>, string> = {
  direct: 'direct messages',
  group: 'group threads',
  threads: 'threads',
  triage: 'triage reports',
};

export interface BuildThreadSectionsArgs<T> {
  /** Non-routine rows, already ordered. */
  rows: readonly T[];
  categories?: readonly ThreadCategory[] | null;
  /** The display kind of a row (resolveThreadDisplayKind at the call site). */
  kindOf: (row: T) => ThreadDisplayKind;
  lang?: ThreadListLang;
  /** Custom ids that fold into a built-in (see parseThreadCategories). */
  foldAliases?: Readonly<Record<string, string>> | null;
  /** Also return empty sections (the sections manager). */
  includeEmpty?: boolean;
}

function kindOfBuiltin(builtin: BuiltinSectionKind): ThreadSectionKind {
  if (builtin === 'direct') return 'direct';
  if (builtin === 'group') return 'group';
  if (builtin === 'triage') return 'triage';
  return 'threads';
}

/**
 * TOTALITY (2026-09-22). Every row handed to this function comes back inside
 * exactly one section — always.
 *
 * It did not used to. Rows were filed into a `bucket` keyed by section id, but
 * the output loop walked `items`, so a row whose id was not in `items` landed
 * in a bucket nothing ever read: it rendered nowhere, silently, with no
 * warning. `categoryOf` only ever returns a member of `items`, so the orphan
 * could only be a NATURAL built-in the caller's list did not carry — which
 * today means `triage-reports`, a CONDITIONAL built-in that `withBuiltinSections`
 * deliberately withholds unless asked for it.
 *
 * The fix is to materialize that section here rather than to thread an
 * `include` list through every call site: a caller that has to agree with this
 * function for rows to survive is exactly the shape that failed. A materialized
 * section is appended, matching `withBuiltinSections`' own rule, so nobody's
 * visible ordering changes — this rung fires only where a row would otherwise
 * have vanished.
 */
export function buildThreadSections<T extends { id: string; category?: string }>(
  args: BuildThreadSectionsArgs<T>,
): ThreadSection<T>[] {
  const items = withBuiltinSections(args.categories);
  const lang = args.lang ?? 'en';
  // Starts as `items` and GROWS — the output loop walks this, never `items`,
  // which is what makes a materialized section reachable.
  const order: ThreadCategory[] = [...items];
  const bucket = new Map<string, T[]>();
  for (const item of items) bucket.set(item.id, []);

  const ensureSection = (id: string): T[] => {
    const existing = bucket.get(id);
    if (existing) return existing;
    const kind = builtinKindOf(id);
    order.push({ id, name: '', order: order.length, ...(kind ? { builtin: kind } : {}) });
    const list: T[] = [];
    bucket.set(id, list);
    return list;
  };

  for (const row of args.rows) {
    if (!row) continue;
    const filed = categoryOf(items, row.category, args.foldAliases);
    // Filed wins; an unfiled row falls to its natural built-in. `filed` is
    // always a member of `items`, so only the natural id can need creating.
    const id = filed ? filed.id : builtinIdForKind(args.kindOf(row));
    (bucket.get(id) ?? ensureSection(id)).push(row);
  }

  const out: ThreadSection<T>[] = [];
  for (const item of order) {
    const rows = bucket.get(item.id) ?? [];
    if (rows.length === 0 && !args.includeEmpty) continue;
    if (item.builtin) {
      const kind = kindOfBuiltin(item.builtin);
      out.push({ key: kind, kind, label: categoryLabel(item, lang), rows, categoryId: item.id, builtin: item.builtin });
    } else {
      out.push({ key: `category:${item.id}`, kind: 'category', label: categoryLabel(item, lang), rows, categoryId: item.id });
    }
  }
  return out;
}

export interface RoutineRowSplit<T> {
  routineRows: T[];
  sectionRows: T[];
}

/**
 * The routines / sections split, as a PARTITION by construction:
 * `routineRows.length + sectionRows.length === rows.length`, and no row is in
 * both (2026-09-22).
 *
 * The rail used to decide the two halves from different inputs — rows were
 * EXCLUDED from sections by `routineRowIds.has(id) || !!t.routineId` while the
 * routines section RENDERED a union built from a tombstone-filtered copy of the
 * same list. A thread whose `routineId` was set but whose id the filter had
 * removed satisfied the exclusion and appeared in neither half: it rendered
 * nowhere at all. Passing the ids the routines section ACTUALLY RENDERS makes
 * the two impossible to disagree.
 *
 * Archived/tombstoned rows are not this function's business — they are filtered
 * out once, before the split, so that "hidden" is a decision about archiving
 * and never an accident of falling between two predicates.
 */
export function splitRoutineRows<T extends { id: string }>(
  rows: readonly T[],
  routineThreadIds: ReadonlySet<string>,
): RoutineRowSplit<T> {
  const routineRows: T[] = [];
  const sectionRows: T[] = [];
  for (const row of rows) {
    if (!row) continue;
    (routineThreadIds.has(row.id) ? routineRows : sectionRows).push(row);
  }
  return { routineRows, sectionRows };
}

/** The stable `data-sidebar-section` / `data-sheet-section` value for a section. */
export function sidebarSectionHook(section: Pick<ThreadSection<unknown>, 'kind' | 'categoryId'>): string {
  if (section.kind === 'category') return `category-${section.categoryId ?? ''}`;
  if (section.kind === 'direct') return 'direct-messages';
  if (section.kind === 'group') return 'group-threads';
  if (section.kind === 'triage') return 'triage-reports';
  return 'threads';
}

/** The type glyph a built-in section header wears. */
export function sectionGlyphKind(kind: ThreadSectionKind): 'direct' | 'group' | 'individual' | 'triage' {
  if (kind === 'direct') return 'direct';
  if (kind === 'group') return 'group';
  if (kind === 'triage') return 'triage';
  return 'individual';
}
